// reports/reportUtils.ts
import * as XLSX from "xlsx";
import type { ReportColumn, ReportFilters, ReportRow, ShipmentMode } from "./Types";
import { MODE_LABEL } from "./Reportconfig";

/**
 * Readable, timezone-safe date formatting.
 * - "2024-05-01"           -> "01 May 2024"        (no tz drift)
 * - "2024-05-01T09:30:00Z" -> "01 May 2024, 09:30" (local time)
 * - empty / invalid        -> ""                   (blank per spec)
 */
export function formatDate(value?: string | number | Date | null): string {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-");
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return `${pad(dt.getDate())} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
  }

  const dt = new Date(value);
  if (isNaN(dt.getTime())) return "";
  return `${pad(dt.getDate())} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}, ${pad(
    dt.getHours()
  )}:${pad(dt.getMinutes())}`;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Single source of truth for a cell's displayed value.
 * Used by BOTH the DataGrid and the exports, so on-screen == file, always.
 */
export function getCellValue(
  row: ReportRow,
  column: ReportColumn,
  mode: ShipmentMode
): string {
  const asRecord = row as unknown as Record<string, unknown>;
  switch (column.kind) {
    case "sno":
      return row.sno != null ? String(row.sno) : "";
    case "mode":
      return MODE_LABEL[mode];
    case "date":
      return formatDate(asRecord[column.field] as string);
    case "flags":
      return (row.flags ?? []).join(", ");
    case "boolean":
      return row.active ? "Active" : "Archived";
    case "number": {
      const v = asRecord[column.field];
      return v === null || v === undefined ? "" : String(v);
    }
    default: {
      const v = asRecord[column.field];
      return v === null || v === undefined ? "" : String(v);
    }
  }
}

/** e.g. air_report_2024-05-01_to_2024-05-31_gen2024-07-27.xlsx */
export function buildFileName(
  mode: ShipmentMode,
  filters: ReportFilters,
  ext: "xlsx" | "csv"
): string {
  const gen = new Date().toISOString().slice(0, 10);
  const start = filters.startDate || "start";
  const end = filters.endDate || "end";
  return `${mode}_report_${start}_to_${end}_gen${gen}.${ext}`;
}

function toMatrix(
  rows: ReportRow[],
  columns: ReportColumn[],
  mode: ShipmentMode
): string[][] {
  const header = columns.map((c) => c.headerName);
  const body = rows.map((row) => columns.map((col) => getCellValue(row, col, mode)));
  return [header, ...body];
}

/** Excel export — full result set, same columns/order as the table. */
export function exportToExcel(
  rows: ReportRow[],
  columns: ReportColumn[],
  mode: ShipmentMode,
  filters: ReportFilters
): void {
  const matrix = toMatrix(rows, columns, mode);
  const ws = XLSX.utils.aoa_to_sheet(matrix);
  ws["!cols"] = columns.map((c) => ({ wch: Math.max(12, c.headerName.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, MODE_LABEL[mode]);
  XLSX.writeFile(wb, buildFileName(mode, filters, "xlsx"));
}

/** CSV export — full result set, RFC-4180 escaping, UTF-8 BOM for Excel. */
export function exportToCsv(
  rows: ReportRow[],
  columns: ReportColumn[],
  mode: ShipmentMode,
  filters: ReportFilters
): void {
  const matrix = toMatrix(rows, columns, mode);
  const csv = matrix.map((r) => r.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, buildFileName(mode, filters, "csv"));
}

function csvEscape(value: string): string {
  const v = value ?? "";
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}